<template>
  <v-dialog v-model="visible" persistent max-width="560">
    <v-card v-if="minor">
      <v-card-title class="headline">
        {{ $t("memberRegistration.parent_guardian.title", { name: minorName }) }}
      </v-card-title>
      <v-card-text>
        <p class="mb-2">
          {{ $t("memberRegistration.parent_guardian.instructions") }}
        </p>
        <v-alert
          type="warning"
          prominent
          dense
          icon="fa fas fa-gavel"
          class="mt-2 mb-3"
        >
          <div class="font-weight-bold">
            {{ $t("memberRegistration.parent_guardian.legal_warning_title") }}
          </div>
          <div>
            {{ $t("memberRegistration.parent_guardian.legal_warning_body") }}
          </div>
        </v-alert>
        <v-radio-group v-model="selected" mandatory>
          <v-radio
            v-for="option in relationshipOptions"
            :key="option.value"
            :label="$t(option.label)"
            :value="option.value"
          />
        </v-radio-group>
        <v-checkbox
          v-model="legalAcknowledged"
          :label="acknowledgementLabel"
          hide-details
          class="mt-0"
        />
        <v-alert
          v-if="isExistingOther"
          border="left"
          colored-border
          color="error"
          elevation="0"
          dense
          class="mt-2"
        >
          {{ $t("memberRegistration.parent_guardian.other_locked") }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn v-if="canLogout" text @click="onLogout">
          {{ $t("layouts.login.logout") }}
        </v-btn>
        <v-btn
          :color="buttonColor || 'primary'"
          :loading="saving"
          :disabled="saving || isExistingOther || !legalAcknowledged || !selected"
          @click="onConfirm"
        >
          {{ $t("common.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import {
  VDialog, VCard, VCardTitle, VCardText, VCardActions,
  VBtn, VSpacer, VAlert, VRadio, VRadioGroup, VCheckbox
} from "vuetify/lib";

export default {
  name: "ParentGuardianDialog",
  components: {
    VDialog, VCard, VCardTitle, VCardText, VCardActions,
    VBtn, VSpacer, VAlert, VRadio, VRadioGroup, VCheckbox
  },
  props: {
    value: { type: Boolean, default: false },
    minor: { type: Object, default: null },
    adultId: { type: [Number, String], default: 0 },
    buttonColor: { type: String, default: "primary" },
    // AMA Waiver Bypass v2 — when the dialog is opened outside the
    // membership-signup stepper (dashboard popup, email-link waiver
    // page) the user cannot dismiss it; their only escape is Logout.
    // In the stepper we leave this false: the user can navigate back
    // via the stepper itself, they don't need an escape hatch here.
    canLogout: { type: Boolean, default: false }
  },
  data() {
    return {
      selected: null,
      saving: false,
      existing: null,
      // AMA Waiver Bypass v2: the user must explicitly acknowledge that
      // their selection is a legally-binding declaration before the
      // Confirm button becomes active.
      legalAcknowledged: false,
      relationshipOptions: [
        { value: "Father", label: "memberRegistration.parent_guardian.father" },
        { value: "Mother", label: "memberRegistration.parent_guardian.mother" },
        { value: "Guardian", label: "memberRegistration.parent_guardian.guardian" },
        { value: "Other", label: "memberRegistration.parent_guardian.other" }
      ]
    };
  },
  computed: {
    visible: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); }
    },
    minorName() {
      if (!this.minor) return "";
      return `${this.minor.fName || this.minor.firstName || ""} ${this.minor.lName || this.minor.lastName || ""}`.trim();
    },
    isExistingOther() {
      return !!(this.existing && String(this.existing.relationshipCode || "").toLowerCase() === "other");
    },
    // Personalizes the "legally binding" acknowledgement to the exact
    // declaration being made: parent (Father/Mother), sole legal custody
    // (Guardian), or explicit non-parent (Other). Until the user picks a
    // relationship we keep the generic wording so the checkbox is never
    // empty when the dialog opens.
    acknowledgementLabel() {
      const minorName = this.minorName;
      if (!this.selected || !minorName) {
        return this.$t("memberRegistration.parent_guardian.legal_acknowledgement");
      }
      if (this.selected === "Father" || this.selected === "Mother") {
        const relKey = this.selected === "Father"
          ? "memberRegistration.parent_guardian.father"
          : "memberRegistration.parent_guardian.mother";
        return this.$t("memberRegistration.parent_guardian.legal_acknowledgement_parent", {
          relationship: this.$t(relKey),
          minorName
        });
      }
      if (this.selected === "Guardian") {
        return this.$t("memberRegistration.parent_guardian.legal_acknowledgement_guardian", { minorName });
      }
      if (this.selected === "Other") {
        return this.$t("memberRegistration.parent_guardian.legal_acknowledgement_other", { minorName });
      }
      return this.$t("memberRegistration.parent_guardian.legal_acknowledgement");
    }
  },
  watch: {
    value(v) {
      if (v) this.loadExisting();
    },
    minor() {
      if (this.value) this.loadExisting();
    }
  },
  methods: {
    async loadExisting() {
      this.existing = null;
      this.selected = null;
      // AMA Waiver Bypass v2: reset the acknowledgement every time the
      // dialog opens so the user cannot reuse a previous check.
      this.legalAcknowledged = false;
      const adultId = Number(this.adultId);
      const minorId = Number(this.minor && this.minor.id);
      if (!adultId || !minorId) return;

      try {
        const url = `${this.$store.getters.env.get('VUE_APP_API_LINK')}/participant/riders/relationship/pair/${adultId}/${minorId}`;
        const response = await axios.get(url);
        if (response && response.data && response.data.success === 1) {
          this.existing = response.data.message || null;
          if (this.existing && this.existing.relationshipCode) {
            this.selected = this.existing.relationshipCode;
          }
        }
      } catch (e) {
        this.existing = null;
      }
    },
    onLogout() {
      this.$emit("logout");
    },
    async onConfirm() {
      if (!this.selected) return;
      const adultId = Number(this.adultId);
      const minorId = Number(this.minor && this.minor.id);
      if (!adultId || !minorId) return;

      // Locally block 'Other' progression: emit blocked event so parent can
      // drop this minor from the selection and surface an admin contact.
      if (this.selected === "Other") {
        this.saving = true;
        try {
          await axios.post(
            `${this.$store.getters.env.get('VUE_APP_API_LINK')}/participant/riders/relationship/pair`,
            { adultId, minorId, relationshipCode: "Other" }
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[ParentGuardianDialog] failed to persist Other", e);
        } finally {
          this.saving = false;
        }
        this.$emit("blocked", { minor: this.minor, adultId, relationshipCode: "Other" });
        this.visible = false;
        return;
      }

      this.saving = true;
      try {
        const response = await axios.post(
          `${this.$store.getters.env.get('VUE_APP_API_LINK')}/participant/riders/relationship/pair`,
          { adultId, minorId, relationshipCode: this.selected }
        );
        if (response && response.data && response.data.success === 1) {
          this.$emit("confirmed", {
            minor: this.minor,
            adultId,
            relationshipCode: this.selected
          });
          this.visible = false;
        } else if (response && response.data && response.data.message === "pair_blocked_other") {
          this.$emit("blocked", { minor: this.minor, adultId, relationshipCode: "Other" });
          this.visible = false;
        }
      } catch (e) {
        if (e && e.response && e.response.data && e.response.data.message === "pair_blocked_other") {
          this.$emit("blocked", { minor: this.minor, adultId, relationshipCode: "Other" });
          this.visible = false;
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
